import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';

export function ValidateFirstOperationDate(): ValidatorFn {
  // Validates if first operation date is later than today
  return (formGroup: AbstractControl): ValidationErrors | null => {
    const spots = (formGroup as UntypedFormGroup).get('spots') as UntypedFormArray;
    if (!spots || spots.length === 0) {
      return null;
    }

    const firstOperation = (spots.at(0).get('operations') as UntypedFormArray).at(0) as UntypedFormGroup;
    const firstOperationBeginDate = firstOperation.get('timespans.begin').value;
    if (!firstOperationBeginDate) {
      return null;
    }

    const firstOperationBegin = new Date(firstOperationBeginDate);
    const today = new Date();
    today.setMinutes(today.getMinutes() + 30);
    if (firstOperationBegin < today) {
      firstOperation.get('timespans.begin').setErrors({ invalidDateBeforeToday: true });
      return { invalidDateBeforeToday: true };
    } else {
      firstOperation.get('timespans.begin').setErrors(null);
    }

    return null;
  };
}

export function ValidateOperationDates(): ValidatorFn {
  // Validates if the begin date is before the end date and at least 30 minutes apart
  return (formGroup: AbstractControl): ValidationErrors | null => {
    const spots = (formGroup as UntypedFormGroup).get('spots') as UntypedFormArray;
    if (!spots || spots.length < 2) {
      return null;
    }

    let lastValidEndDate: Date | null = null;

    for (let i = 0; i < spots.length; i++) {
      const operations = (spots.at(i).get('operations') as UntypedFormArray).controls;

      for (let j = 0; j < operations.length; j++) {
        const currentOperation = operations[j] as UntypedFormGroup;
        const currentBeginDateValue = currentOperation.get('timespans.begin').value;
        const currentEndDateValue = currentOperation.get('timespans.end').value;

        if (!currentBeginDateValue) {
          continue;
        }

        const currentBeginDate = new Date(currentBeginDateValue);
        const currentEndDate = currentEndDateValue ? new Date(currentEndDateValue) : null;

        if (
          currentBeginDateValue &&
          currentEndDateValue &&
          currentBeginDate.getTime() >= currentEndDate.getTime() - 30 * 60000
        ) {
          currentOperation.get('timespans.begin').setErrors({ invalidDate30MinApart: true });
          currentOperation.get('timespans.end').setErrors({ invalidDate30MinApart: true });
          return { invalidOperationDates: true };
        }

        if (lastValidEndDate && currentBeginDate <= lastValidEndDate) {
          currentOperation.get('timespans.begin').setErrors({ invalidChronologicalOrder: true });
          return { invalidChronologicalOrder: true };
        } else {
          currentOperation.get('timespans.begin').setErrors(null);
        }

        if (currentEndDate) {
          if (currentBeginDate >= currentEndDate) {
            currentOperation.get('timespans.end').setErrors({ invalidChronologicalOrder: true });
            return { invalidChronologicalOrder: true };
          } else {
            currentOperation.get('timespans.end').setErrors(null);
          }
          lastValidEndDate = currentEndDate;
        } else {
          lastValidEndDate = currentBeginDate;
        }
      }
    }

    return null;
  };
}

export function validateFirstAndLastOperationsTypes(): ValidatorFn {
  // Validates if the first operation is loading and the last operation is unloading
  return (formGroup: AbstractControl): ValidationErrors | null => {
    const spots = (formGroup as UntypedFormGroup).get('spots') as UntypedFormArray;
    if (spots.length < 2) {
      return null;
    }
    const firstSpotOperations: AbstractControl[] = (spots.at(0).get('operations') as UntypedFormArray)
      .controls;
    const lastSpotOperations: AbstractControl[] = (
      spots.at(spots.length - 1).get('operations') as UntypedFormArray
    ).controls;
    if (firstSpotOperations.length === 0 || lastSpotOperations.length === 0) {
      return null;
    }
    const firstOperationControl: UntypedFormGroup = firstSpotOperations[0] as UntypedFormGroup;
    const lastOperationControl: UntypedFormGroup = lastSpotOperations[
      lastSpotOperations.length - 1
    ] as UntypedFormGroup;
    const firstOperationType = firstOperationControl.get('type').value;
    const lastOperationType = lastOperationControl.get('type').value;
    let hasError = false;
    if (firstOperationType !== 'loading') {
      firstOperationControl.get('type').setErrors({ invalidFirstOperation: true });
      hasError = true;
    } else {
      firstOperationControl.get('type').setErrors(null);
    }
    if (lastOperationType !== 'unloading') {
      lastOperationControl.get('type').setErrors({ invalidLastOperation: true });
      hasError = true;
    } else {
      lastOperationControl.get('type').setErrors(null);
    }
    return hasError ? { invalidOperationSequence: true } : null;
  };
}

export function validateOperationHours(): ValidatorFn {
  // Validates if the begin date is before the end date and at least 30 minutes apart in operation
  return (control: AbstractControl): ValidationErrors | null => {
    const beginControl = control.get('begin');
    const endControl = control.get('end');

    if (!beginControl || !endControl) {
      return null;
    }

    const begin = beginControl.value;
    const end = endControl.value;
    if (!begin || !end) {
      return null;
    }
    const beginDate = new Date(begin);
    const endDate = new Date(end);
    if (beginDate.getTime() >= endDate.getTime() - 30 * 60000) {
      beginControl.setErrors({ invalidDate30MinApart: true });
      endControl.setErrors({ invalidDate30MinApart: true });
      return { invalidOperationHours: true };
    } else {
      beginControl.setErrors(null);
      endControl.setErrors(null);
    }
    return null;
  };
}
